export const REPORT_CATEGORIES = [
    {
        id: 'hazard',
        label: 'Hazard',
        iconBase: 'MAP_QUESTION',
        iconSrc: '/img/report-icons/map-question.svg',
        color: '#f59f00',
    },
    {
        id: 'accident',
        label: 'Accident',
        iconBase: 'CAR_CRASH',
        iconSrc: '/img/report-icons/car-crash.svg',
        color: '#f59f00',
    },
    {
        id: 'weather',
        label: 'Weather',
        iconBase: 'CLOUD_STORM',
        iconSrc: '/img/report-icons/cloud-storm.svg',
        color: '#f59f00',
    },
    {
        id: 'wildlife',
        label: 'Wildlife',
        iconBase: 'FISH',
        iconSrc: '/img/report-icons/fish.svg',
        color: '#f59f00',
    },
];

export const REPORT_CATEGORY_BY_ID = REPORT_CATEGORIES.reduce((acc, category) => {
    acc[category.id] = category;
    return acc;
}, {});

// Backwards-compatible aliases for renamed report types.
REPORT_CATEGORY_BY_ID.obstacle = REPORT_CATEGORY_BY_ID.hazard;
REPORT_CATEGORY_BY_ID.uncharted_hazard = REPORT_CATEGORY_BY_ID.hazard;
REPORT_CATEGORY_BY_ID.fish = REPORT_CATEGORY_BY_ID.wildlife;

// Zeus Track disc radii (px): 32px fill disc + white/outer borders + soft shadow
export const USER_REPORT_SHADOW_RADIUS = 20;
export const USER_REPORT_OUTER_RADIUS = 18;
export const USER_REPORT_WHITE_RADIUS = 16;
export const USER_REPORT_FILL_RADIUS = 14;
export const USER_REPORT_MIN_ZOOM = 11;
export const USER_REPORT_ICON_SIZE = 0.7;

// Scale radii 50%→100% from z11→z14
export function userReportScaledRadiusExpression(baseRadius) {
    return [
        'interpolate',
        ['linear'],
        ['zoom'],
        USER_REPORT_MIN_ZOOM, baseRadius * 0.5,
        14, baseRadius,
    ];
}

export function userReportPalette(chartTheme = 'day') {
    if (chartTheme === 'night') {
        return { fill: '#8a4500', ring: '#868e96' };
    }
    if (chartTheme === 'dusk') {
        return { fill: '#e67700', ring: '#dee2e6' };
    }
    return { fill: '#f59f00', ring: '#ffffff' };
}

export function userReportColorExpression(chartTheme = 'day') {
    const fill = userReportPalette(chartTheme).fill;
    return [
        'match',
        ['get', 'report_type'],
        'hazard', fill,
        'obstacle', fill,
        'uncharted_hazard', fill,
        'accident', fill,
        'weather', fill,
        'wildlife', fill,
        'fish', fill,
        fill,
    ];
}

// Glyphs sit on colored Track-style discs — white on day, black on dusk/night.
export function userReportIconImageExpression(chartTheme = 'day') {
    const iconFor = (base) => (
        chartTheme === 'day' ? base + '_WHITE' : base + '_BLACK'
    );
    return [
        'match',
        ['get', 'report_type'],
        'hazard', iconFor('MAP_QUESTION'),
        'obstacle', iconFor('MAP_QUESTION'),
        'uncharted_hazard', iconFor('MAP_QUESTION'),
        'accident', iconFor('CAR_CRASH'),
        'weather', iconFor('CLOUD_STORM'),
        'wildlife', iconFor('FISH'),
        'fish', iconFor('FISH'),
        iconFor('MAP_QUESTION'),
    ];
}

// "mm/dd hh:mm" — matches MVT label time formatting.
export function formatUserReportTime(createdAt = new Date()) {
    const date = createdAt instanceof Date ? createdAt : new Date(createdAt);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return month + '/' + day + ' ' + hours + ':' + minutes;
}

// "<type> - mm/dd hh:mm" — matches MVT label formatting.
export function formatUserReportLabel(reportType, createdAt = new Date()) {
    const typeLabel = REPORT_CATEGORY_BY_ID[reportType]?.label || reportType || 'Report';
    const time = formatUserReportTime(createdAt);
    if (!time) {
        return typeLabel;
    }
    return typeLabel + ' - ' + time;
}

export function userReportShadowPaint({ opacity = 1 } = {}) {
    return {
        'circle-radius': userReportScaledRadiusExpression(USER_REPORT_SHADOW_RADIUS),
        'circle-color': '#000000',
        'circle-opacity': 0.12 * opacity,
    };
}

export function userReportOuterPaint({ opacity = 1 } = {}) {
    return {
        'circle-radius': userReportScaledRadiusExpression(USER_REPORT_OUTER_RADIUS),
        'circle-color': '#000000',
        'circle-opacity': 0.1 * opacity,
    };
}

export function userReportWhitePaint({ opacity = 1, chartTheme = 'day' } = {}) {
    return {
        'circle-radius': userReportScaledRadiusExpression(USER_REPORT_WHITE_RADIUS),
        'circle-color': userReportPalette(chartTheme).ring,
        'circle-opacity': 1 * opacity,
    };
}

export function userReportFillPaint({ opacity = 1, chartTheme = 'day' } = {}) {
    return {
        'circle-radius': userReportScaledRadiusExpression(USER_REPORT_FILL_RADIUS),
        'circle-color': userReportColorExpression(chartTheme),
        'circle-opacity': 0.8 * opacity,
    };
}

export function userReportLabelLayout(chartTheme = 'day', font = 'open-sans') {
    return {
        'icon-image': userReportIconImageExpression(chartTheme),
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-size': [
            'interpolate',
            ['linear'],
            ['zoom'],
            USER_REPORT_MIN_ZOOM, USER_REPORT_ICON_SIZE * 0.5,
            14, USER_REPORT_ICON_SIZE,
        ],
        'text-field': ['get', 'label'],
        'text-font': [font],
        'text-size': 11,
        'text-offset': [0, 1.8],
        'text-anchor': 'top',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-optional': true,
    };
}

export function userReportLabelPaint(chartTheme = 'day', { opacity = 1 } = {}) {
    const isDay = chartTheme === 'day';
    return {
        'icon-opacity': opacity,
        'text-opacity': [
            'step',
            ['zoom'],
            0,
            14, opacity,
        ],
        'text-color': isDay ? '#111111' : '#f5f5f5',
        'text-halo-color': isDay ? '#ffffff' : '#111111',
        'text-halo-width': 1.5,
    };
}

export function getUserReportDescription(reportOrProps) {
    if (!reportOrProps) {
        return '';
    }
    if (typeof reportOrProps.description === 'string' && reportOrProps.description.trim()) {
        return reportOrProps.description.trim();
    }
    let properties = reportOrProps.properties;
    if (typeof properties === 'string') {
        try {
            properties = JSON.parse(properties);
        } catch (e) {
            properties = null;
        }
    }
    if (properties && typeof properties.description === 'string') {
        return properties.description.trim();
    }
    return '';
}

export function draftReportToGeoJson(draft) {
    if (!draft || draft.latitude == null || draft.longitude == null) {
        return { type: 'FeatureCollection', features: [] };
    }
    const reportType = draft.category || 'hazard';
    return {
        type: 'FeatureCollection',
        features: [
            {
                type: 'Feature',
                // Changing id forces Mapbox symbol layers to treat this as a new feature
                // when only coordinates change (otherwise the icon can stick at the old point).
                id: draft.id || Date.now(),
                properties: {
                    report_type: reportType,
                    label: formatUserReportLabel(reportType, new Date()),
                    _rev: Date.now(),
                },
                geometry: {
                    type: 'Point',
                    coordinates: [draft.longitude, draft.latitude],
                },
            },
        ],
    };
}

// Bust HTTP tile cache and force Mapbox to re-request userReports tiles immediately.
// Do not use the URL constructor here — it percent-encodes {z}/{x}/{y} placeholders.
export function reloadUserReportsSource(map) {
    if (!map) {
        return;
    }
    try {
        const source = map.getSource('userReports');
        if (source && typeof source.setTiles === 'function') {
            let currentTiles = source.tiles;
            if ((!currentTiles || !currentTiles.length) && typeof source.serialize === 'function') {
                currentTiles = source.serialize().tiles || [];
            }
            if (currentTiles && currentTiles.length) {
                const bust = String(Date.now());
                const nextTiles = currentTiles.map((url) => {
                    const raw = decodeURIComponent(String(url));
                    const withoutBust = raw
                        .replace(/[?&]_=\d+/g, '')
                        .replace(/[?&]$/, '');
                    return withoutBust + (withoutBust.includes('?') ? '&' : '?') + '_=' + bust;
                });
                source.setTiles(nextTiles);
            }
        }
        const cache = map.style?.sourceCaches?.userReports;
        if (cache) {
            cache.clearTiles();
            cache.update(map.transform);
        }
        map.triggerRepaint();
    } catch (e) {
        console.log('Failed to reload user report tiles', e);
    }
}
